// CommentaryCard — the manager-owned narrative on the report summary: overall
// summary, key risks, next period focus, and the declared RAG.
//
// Everything else on the report is derived from records; this is the one place
// a person states a view. That shapes two behaviours:
//
//   1. The edit affordance is gated to admin and editor. A viewer sees the
//      narrative with no control at all, and a global admin sees the same —
//      the server refuses their writes because report rows live under tenant
//      partitions, so offering the button would promise something the API
//      would then refuse.
//   2. A stale-version 409 never discards the author's draft. Someone has
//      spent real thought on the words; the card keeps them on screen and
//      asks which of two explicit things to do — look at what landed, or
//      write over it — rather than picking one silently.
//
// The form seeds from props at the moment Edit is pressed, not at mount, so a
// card that has been sitting open on a stale render cannot seed an empty
// draft over a populated record.

import { useState } from "react";
import { clsx } from "clsx";
import Button from "../../common/Button";
import Alert from "../../common/Alert";
import ReportSection, { UnavailableNotice } from "./ReportSection";
import { useAuth } from "../../../contexts/AuthContext";
import {
  useFetchLatestCommentary,
  useUpdateReportCommentary,
} from "../../../hooks/useContractReport";
import { ApiError } from "../../../services/api";
import { formatDayMonthYearLocal } from "../../../utils/formatShortDate";
import {
  COMMENTARY_FIELD_MAX,
  type ManagerRag,
  type ReportCommentary,
  type SummaryReportModel,
} from "../../../types/report";

const SECTION_LABEL = "Manager commentary";

type StoredCommentary = { status: "ok" } & ReportCommentary;

const RAG_CHOICES: ReadonlyArray<{ value: ManagerRag; label: string }> = [
  { value: "green", label: "Green" },
  { value: "amber", label: "Amber" },
  { value: "red", label: "Red" },
  { value: null, label: "Not assessed" },
];

const NARRATIVE_FIELDS = [
  { key: "overallSummary", label: "Overall summary" },
  { key: "keyRisks", label: "Key risks" },
  { key: "nextFocus", label: "Next period focus" },
] as const;

type NarrativeKey = (typeof NARRATIVE_FIELDS)[number]["key"];

interface Draft {
  overallSummary: string;
  keyRisks: string;
  nextFocus: string;
  managerRag: ManagerRag;
}

export interface CommentaryCardProps {
  contractId: string;
  commentary: SummaryReportModel["commentary"];
}

export default function CommentaryCard({
  contractId,
  commentary,
}: CommentaryCardProps) {
  if (commentary.status === "unavailable") {
    return (
      <ReportSection id="commentary" title={SECTION_LABEL}>
        <UnavailableNotice label={SECTION_LABEL} />
      </ReportSection>
    );
  }
  return <CommentaryBody contractId={contractId} commentary={commentary} />;
}

function CommentaryBody({
  contractId,
  commentary,
}: {
  contractId: string;
  commentary: StoredCommentary;
}) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "editor";

  const [draft, setDraft] = useState<Draft | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [savedVersion, setSavedVersion] = useState<StoredCommentary | null>(
    null,
  );
  const [saveError, setSaveError] = useState<string | null>(null);

  const updateMutation = useUpdateReportCommentary();
  const fetchLatest = useFetchLatestCommentary();

  function startEditing() {
    setDraft({
      overallSummary: commentary.overallSummary,
      keyRisks: commentary.keyRisks,
      nextFocus: commentary.nextFocus,
      managerRag: commentary.managerRag,
    });
    setConflict(null);
    setSavedVersion(null);
    setSaveError(null);
  }

  function stopEditing() {
    setDraft(null);
    setConflict(null);
    setSavedVersion(null);
    setSaveError(null);
  }

  /** Writes `current` at `version`. Every failure path keeps the draft on
   *  screen: a 409 asks the author to choose, anything else reports the
   *  problem. Losing typed narrative to a failed request is not acceptable
   *  here — nobody remembers the paragraph they just wrote. */
  async function persist(current: Draft, version: number) {
    setSaveError(null);
    try {
      await updateMutation.mutateAsync({
        contractId,
        input: { ...current, version },
      });
      stopEditing();
    } catch (err) {
      // apiRequest rejects with an ApiError for every outcome including a
      // network failure (which it wraps at status 0), so there is no other
      // shape to narrow against here.
      const apiError = err as ApiError;
      if (apiError.status === 409) {
        setConflict(apiError.message);
        setSavedVersion(null);
        return;
      }
      setSaveError(apiError.message);
    }
  }

  /** Reads the commentary that actually landed. Returns null and reports the
   *  problem when it cannot be read — whether the section came back
   *  unavailable or the request itself failed. Both leave the draft and the
   *  conflict panel exactly where they are, so the author can retry rather
   *  than losing the recovery path along with the words. */
  async function readLanded(
    unreadableMessage: string,
  ): Promise<StoredCommentary | null> {
    setSaveError(null);
    let latest: SummaryReportModel["commentary"];
    try {
      latest = await fetchLatest(contractId);
    } catch (err) {
      setSaveError((err as ApiError).message);
      return null;
    }
    if (latest.status === "unavailable") {
      setSaveError(unreadableMessage);
      return null;
    }
    return latest;
  }

  async function handleReviewSaved() {
    const latest = await readLanded(
      "The saved version couldn't be read. Copy your draft somewhere safe before retrying.",
    );
    if (latest === null) return;
    setSavedVersion(latest);
  }

  async function handleOverwrite(current: Draft) {
    const latest = await readLanded(
      "The saved version couldn't be read, so there is no version to write over. Copy your draft somewhere safe before retrying.",
    );
    if (latest === null) return;
    await persist(current, latest.version);
  }

  const editAction =
    canEdit && draft === null ? (
      <Button variant="secondary" size="sm" onClick={startEditing}>
        Edit commentary
      </Button>
    ) : undefined;

  return (
    <ReportSection id="commentary" title={SECTION_LABEL} action={editAction}>
      {draft === null ? (
        <ReadOnlyNarrative commentary={commentary} />
      ) : (
        <div className="space-y-4">
          {conflict !== null && (
            <ConflictPanel
              message={conflict}
              onReview={handleReviewSaved}
              onOverwrite={() => handleOverwrite(draft)}
              busy={updateMutation.isPending}
            />
          )}
          {saveError !== null && <Alert variant="error">{saveError}</Alert>}
          {savedVersion !== null && <SavedVersionPanel saved={savedVersion} />}

          {NARRATIVE_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <label className="label" htmlFor={`commentary-${key}`}>
                {label}
              </label>
              <textarea
                id={`commentary-${key}`}
                className="textarea"
                rows={4}
                maxLength={COMMENTARY_FIELD_MAX}
                value={draft[key as NarrativeKey]}
                onChange={(e) =>
                  setDraft({ ...draft, [key]: e.target.value })
                }
              />
            </div>
          ))}

          <RagSelector
            value={draft.managerRag}
            onChange={(managerRag) => setDraft({ ...draft, managerRag })}
          />

          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              loading={updateMutation.isPending}
              onClick={() => persist(draft, commentary.version)}
            >
              Save commentary
            </Button>
            <Button variant="ghost" size="sm" onClick={stopEditing}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </ReportSection>
  );
}

/** An unwritten block reads as an em dash rather than as blank space, so a
 *  section the author left empty is visibly empty rather than looking like a
 *  rendering fault. */
function orDash(value: string): string {
  return value === "" ? "—" : value;
}

function ReadOnlyNarrative({ commentary }: { commentary: StoredCommentary }) {
  return (
    <div className="space-y-4">
      {NARRATIVE_FIELDS.map(({ key, label }) => (
        <div key={key}>
          <p className="text-caption font-medium text-slate-500 dark:text-slate-400">
            {label}
          </p>
          <p className="mt-0.5 whitespace-pre-wrap text-body-sm text-slate-900 dark:text-slate-100">
            {orDash(commentary[key])}
          </p>
        </div>
      ))}
      <FreshnessLine commentary={commentary} />
    </div>
  );
}

const TIME_FORMAT = new Intl.DateTimeFormat("en-AU", {
  hour: "numeric",
  minute: "2-digit",
});

function FreshnessLine({ commentary }: { commentary: StoredCommentary }) {
  const { updatedAt, updatedByName } = commentary;
  return (
    <p
      data-testid="commentary-freshness"
      className="border-t border-slate-100 pt-3 text-caption text-slate-500 dark:border-slate-700 dark:text-slate-400"
    >
      {updatedAt === null
        ? "No commentary recorded yet"
        : `Last updated by ${updatedByName} · ${formatDayMonthYearLocal(updatedAt)} at ${TIME_FORMAT.format(new Date(updatedAt))}`}
    </p>
  );
}

function RagSelector({
  value,
  onChange,
}: {
  value: ManagerRag;
  onChange: (value: ManagerRag) => void;
}) {
  return (
    <div>
      <p className="label" id="commentary-rag-label">
        Manager rating
      </p>
      <div
        role="radiogroup"
        aria-labelledby="commentary-rag-label"
        className="flex flex-wrap gap-4"
      >
        {RAG_CHOICES.map((choice) => {
          const id = `commentary-rag-${choice.value ?? "none"}`;
          return (
            <span key={id} className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                id={id}
                name="commentary-rag"
                // `accent-brand-600` and not the `text-brand-600` form-plugin
                // idiom: @tailwindcss/forms is not installed, so the latter
                // leaves the control painting the browser default.
                className="h-4 w-4 accent-brand-600"
                checked={value === choice.value}
                onChange={() => onChange(choice.value)}
              />
              <label
                htmlFor={id}
                className="text-body-sm text-slate-700 dark:text-slate-300"
              >
                {choice.label}
              </label>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function ConflictPanel({
  message,
  onReview,
  onOverwrite,
  busy,
}: {
  message: string;
  onReview: () => void;
  onOverwrite: () => void;
  busy: boolean;
}) {
  return (
    <div
      role="alert"
      data-testid="commentary-conflict"
      className={clsx(
        "rounded-lg border border-amber-200 bg-amber-50 px-4 py-3",
        "dark:border-amber-800 dark:bg-amber-900/20",
      )}
    >
      <p className="text-body-sm font-medium text-amber-800 dark:text-amber-300">
        {message}
      </p>
      <p className="mt-1 text-caption text-amber-700 dark:text-amber-400">
        Your draft is untouched below. Read what landed before deciding, or
        write over it.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onClick={onReview}>
          Review the saved version
        </Button>
        <Button
          variant="secondary"
          size="sm"
          loading={busy}
          onClick={onOverwrite}
        >
          Save over the saved version
        </Button>
      </div>
    </div>
  );
}

function SavedVersionPanel({ saved }: { saved: StoredCommentary }) {
  return (
    <div
      data-testid="commentary-saved-version"
      className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60"
    >
      <p className="text-caption font-medium text-slate-500 dark:text-slate-400">
        Saved version by {saved.updatedByName}
      </p>
      {NARRATIVE_FIELDS.map(({ key, label }) => (
        <div key={key} className="mt-2">
          <p className="text-caption text-slate-500 dark:text-slate-400">
            {label}
          </p>
          <p className="whitespace-pre-wrap text-body-sm text-slate-700 dark:text-slate-200">
            {orDash(saved[key])}
          </p>
        </div>
      ))}
    </div>
  );
}
